import React, { Fragment, useRef, useCallback, useState, useMemo } from 'react'
import { Field, Form, Formik } from 'formik'
import TextField from 'components/Common/TextField'
import { Typography, Box } from '@mui/material'
import Button from 'components/Common/Button'
import { store } from 'reduxStore'
import { AccountFormField, AccountFormLayout } from 'components/Common/AccountFormLayout'
import PhoneField from 'components/Common/PhoneField'
import { CodeListDropdown } from 'components/Common/CodeListDropdown'
import * as yup from 'yup'
import AlertSuccess from './AlertSuccess'
import api from 'api'
import UserContent from './UserContent'
import { useDispatch } from 'react-redux'
import { BusinessAvatar } from './BusinessAvatar'
import AddressFields from 'components/Common/AddressFields'
import ZendeskLink from 'components/Common/ZendeskLink'
import routes from '../../api/routes'
import BusinessFormControls from './BusinessFormControls'
import { PRIMARY_BUTTON, SECONDARY_BUTTON, DISABLED_BUTTON } from 'themes/theme'
import { styled } from '@mui/material/styles'

const AvatarContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  maxWidth: 180,
  gap: '15px',
}))

const schema = yup.object({
  businessName: yup.string().required('Company Name is required'),
  address: yup.string().required('Address is required'),
  city: yup.string().required('City is required'),
  state: yup.string().required('State is required'),
  zip: yup.string().required('Zip Code is required'),
  countryCode: yup.string().notRequired(),
  stateCode: yup.string().notRequired(),
  lat: yup.number(),
  lon: yup.number(),
  country: yup.string(),
  businessId: yup.string().required(),
  businessEmail: yup.string().email('Invalid Email Address'),
  businessPhone: yup.string().notRequired(),
  businessFax: yup.string().notRequired(),
  website: yup
    .string()
    // .matches(
    //   /((https?):\/\/)?(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/,
    //   'Please, enter valid url'
    // )
    .notRequired(),
  emailOrPhoneRequired: yup.string(),
})

const initialValues = {
  country: '',
  businessName: '',
  address: '',
  city: '',
  state: '',
  countryCode: '',
  stateCode: '',
  zip: '',
  lat: 0,
  lon: 0,
  businessId: '',
  businessFax: '',
  businessEmail: '',
  businessPhone: '',
  website: '',
  emailOrPhoneRequired: '',
}

const Profile = ({ model, business, setBusiness }) => {
  const formik = useRef(null)
  const [businessPhotoId, setPhotoId] = useState(null)
  const isFreeUser = model?.accountType === 'free'
  const [submitted, setSubmitted] = useState(false)
  const dispatch = useDispatch()
  const inputFile = useRef()
  const user = store.getState().userReducer.user
  const bPhone = business?.details?.businessPhone || ''
  const maskPhone = bPhone ? bPhone.slice(0, 3) + -+bPhone.slice(3, 6) + -+bPhone.slice(6, 10) : ''
  const bFax = business?.details?.businessFax || business?.fax || ''
  const maskFax = bFax ? bFax.slice(0, 3) + -+bFax.slice(3, 6) + -+bFax.slice(6, 10) : ''

  const fetchChartData = async () => {
    await api.getDashboardInfo()
  }

  const onFileFieldChange = useCallback(
    async event => {
      if (event.currentTarget.files && business) {
        const file = event.currentTarget.files[0]
        event.target.value = ''
        const formData = new FormData()
        formData.append('file', file)
        formData.append('business_id', business.details.businessId)

        try {
          const bphotoId = await api.updateAvatar(formData, routes.businessAvatar)
          setPhotoId(bphotoId.fileId)
          const updatedBusiness = await api.getUsersBusinessByExtId(business.ext_id)
          setBusiness(updatedBusiness)
          setTimeout(fetchChartData, 2000)
        } catch (e) {
          console.log(e)
        }
      }
    },
    [setBusiness, business],
  )

  const formValue = useMemo(() => {
    if (business === null) return initialValues
    return {
      address: business.details.addressStreet || business.address,
      country: business.details.addressCountry || business.country,
      lat: business.details.addressLat || business.lat || 0,
      lon: business.details.addressLon || business.lon || 0,
      businessName: business.details.businessName || business.company,
      street: business.details.addressStreet || business.address,
      city: business.details.addressCity || business.city,
      state: business.details.addressState || business.state,
      zip: business.details.addressZip || business.zip,
      businessId: business.details.businessId,
      businessPhone: maskPhone,
      businessFax: maskFax,
      website: business.details.website || '',
      businessEmail: business.details.businessEmail || '',
      emailOrPhoneRequired: '',
      countryCode: business.details.addressCountry || business.country,
      stateCode: business.details.addressState || business.state,
    }
  }, [business, maskPhone, maskFax])

  const deleteBusinessAvatar = useCallback(async () => {
    if (business) {
      try {
        await api.deleteAvatar(routes.businessAvatarDel, { businessId: business.details.businessId })
        setPhotoId(null)
        const updatedBusiness = await api.getUsersBusinessByExtId(business.ext_id)
        setBusiness(updatedBusiness)
        setTimeout(fetchChartData, 2000)
      } catch (e) {
        console.log(e)
      }
    }
  }, [setBusiness, business])

  const onCountryChange = (e, setFieldValue, getCountriesCode) => {
    const validCharsRegex = /[a-zA-Z]/
    const targetValue = e.target.value
    if (!targetValue.match(validCharsRegex) || !e) {
      getCountriesCode('')
      setFieldValue('countryCode', '')
    } else if (targetValue.length > 3) {
      const subString = targetValue.substring(0, 3)
      getCountriesCode(subString)
      setFieldValue('countryCode', subString)
    } else {
      getCountriesCode(targetValue)
      setFieldValue('countryCode', targetValue)
    }
  }

  const onStateChange = (e, setFieldValue, getCountriesCode) => {
    const validCharsRegex = /[a-zA-Z]/
    const targetValue = e.target.value
    if (!targetValue.match(validCharsRegex) || !e) {
      getCountriesCode('')
      setFieldValue('stateCode', '')
    } else if (targetValue.length > 2) {
      const subString = targetValue.substring(0, 2)
      getCountriesCode(subString)
      setFieldValue('stateCode', subString)
    } else {
      getCountriesCode(targetValue)
      setFieldValue('stateCode', targetValue)
    }
  }

  const handleCountryFieldKeyPress = e => {
    const validCharsRegex = /[a-zA-Z]/

    if (e.target.value.length >= 3 || !e.key.match(validCharsRegex)) {
      e.preventDefault()
    }
  }

  const handleStateFieldKeyPress = e => {
    const validCharsRegex = /[a-zA-Z]/
    if (e.target.value.length >= 2 || !e.key.match(validCharsRegex)) {
      e.preventDefault()
    }
  }

  const onAvatarAddClick = () => {
    inputFile.current.click()
  }

  const onFormSubmit = useCallback(
    async (values, formikHelpers) => {
      formikHelpers.setSubmitting(true)
      const body = {
        ...values,
        addressStreet: values.address,
        addressState: values.stateCode,
        addressZip: values.zip,
        addressCity: values.city,
        addressCountry: values.countryCode,
        addressLat: values.lat,
        addressLon: values.lon,
        website: values.website,
        businessPhone: values.businessPhone.replaceAll('-', ''),
        businessFax: values.businessFax.replaceAll('-', ''),
        businessEmail: values.businessEmail,
      }

      try {
        await api.updateMyBusinessProfile(body)
        const updatedBusiness = business && (await api.getUsersBusinessByExtId(business.ext_id))
        setBusiness(updatedBusiness)
        setSubmitted(true)
        dispatch({ type: 'resetBusinessSelected' })
        setTimeout(fetchChartData, 2000)
      } catch (error) {
        console.log(error)
      } finally {
        formikHelpers.setSubmitting(false)
      }
    },
    [setBusiness, dispatch, business],
  )

  const emailPhoneValidation = values => {
    if (!!(!values.businessEmail && !values.businessPhone)) {
      return {
        emailOrPhoneRequired: '“Email” or “Business Phone Number” is required to make changes.',
        businessEmail: ' ',
        businessPhone: ' ',
      }
    }
  }

  if (user?.kybValidated === undefined) {
    return (
      <UserContent
        label={user.accountType === 'free' ? 'Subscribe' : 'Find Now'}
        content={
          user.accountType === 'free'
            ? 'Become a premium member to claim your business, add additional fields on your profile and monitor businesses you are interested in.'
            : 'Find your business to claim it, add additional fields and enhance your profile for suppliers, customers and partners.'
        }
      />
    )
  }

  return (
    <>
      <Formik
        innerRef={formik}
        validationSchema={schema}
        initialValues={formValue}
        onSubmit={onFormSubmit}
        enableReinitialize
        validate={emailPhoneValidation}
      >
        {({ dirty, isSubmitting, setFieldValue, errors }) => {
          return (
            <Form style={{ flex: 1 }}>
              <BusinessFormControls user={user} model={business} isSubmitting={isSubmitting} dirty={dirty} />
              <AccountFormLayout
                rightSideFullSize
                hideDividers
                leftSide={
                  <Fragment>
                    <Box position="relative" className="customAuthInput">
                      <AccountFormField>
                        <Field name="businessName">
                          {({ field, meta }) => (
                            <TextField
                              {...field}
                              label="Company Name"
                              required
                              fullWidth
                              borderLess
                              error={Boolean(meta.touched && meta.error)}
                              helperText={meta.touched && meta.error}
                              disabled={isFreeUser}
                            />
                          )}
                        </Field>
                      </AccountFormField>
                      <AccountFormField>
                        <Field name="businessEmail">
                          {({ field, meta }) => (
                            <TextField
                              {...field}
                              fullWidth
                              borderLess
                              label="Email"
                              required
                              error={Boolean(meta.touched && meta.error)}
                              helperText={meta.touched && meta.error}
                              disabled={isFreeUser}
                            />
                          )}
                        </Field>
                      </AccountFormField>
                      <AccountFormField>
                        <Field name="website">
                          {({ field, meta }) => (
                            <TextField
                              {...field}
                              type="Text"
                              label="Website"
                              placeholder="https://dashboard.app.markaaz.com"
                              fullWidth
                              borderLess
                              error={Boolean(meta.touched && meta.error)}
                              helperText={meta.touched && meta.error}
                              disabled={isFreeUser}
                            />
                          )}
                        </Field>
                      </AccountFormField>
                    </Box>
                    <Box className="customAuthInput">
                      <AccountFormField>
                        <Field name="businessPhone">
                          {({ field, meta }) => (
                            <PhoneField
                              {...field}
                              fullWidth
                              borderLess
                              required
                              isMasked
                              label="Business Phone Number"
                              error={Boolean(meta.touched && meta.error)}
                              helperText={meta.touched && meta.error}
                              disabled={isFreeUser}
                            />
                          )}
                        </Field>
                      </AccountFormField>
                      <AccountFormField>
                        <Field name="businessFax">
                          {({ field, meta }) => (
                            <PhoneField
                              {...field}
                              fullWidth
                              borderLess
                              isMasked
                              label="Business Fax Number"
                              error={Boolean(meta.touched && meta.error)}
                              helperText={meta.touched && meta.error}
                              disabled={isFreeUser}
                            />
                          )}
                        </Field>
                      </AccountFormField>
                    </Box>
                    <Box className="customAuthInput">
                      <AccountFormField>
                        <AddressFields
                          setFieldValue={setFieldValue}
                          initValues={formValue}
                          disabled={isFreeUser}
                          borderLess
                        />
                      </AccountFormField>
                      <AccountFormField>
                        <Field name="city">
                          {({ field, meta }) => (
                            <TextField
                              error={Boolean(meta.touched && meta.error)}
                              helperText={meta.touched && meta.error}
                              {...field}
                              label="City"
                              required
                              fullWidth
                              borderLess
                              disabled={isFreeUser}
                            />
                          )}
                        </Field>
                      </AccountFormField>
                      <AccountFormField>
                        <CodeListDropdown
                          name="stateCode"
                          onChange={onStateChange}
                          setFieldValue={setFieldValue}
                          helperText="Field requires two letter state abbreviations"
                          label="State"
                          required
                          placeholder="ex TX"
                          onKeyPress={handleStateFieldKeyPress}
                          disabled={isFreeUser}
                          borderLess
                        />
                      </AccountFormField>
                      <AccountFormField>
                        <Field name="zip">
                          {({ field, meta }) => (
                            <TextField
                              error={Boolean(meta.touched && meta.error)}
                              helperText={meta.touched && meta.error}
                              {...field}
                              label="Zip Code"
                              required
                              fullWidth
                              borderLess
                              disabled={isFreeUser}
                            />
                          )}
                        </Field>
                      </AccountFormField>
                      <AccountFormField style={{ height: 'unset' }}>
                        <CodeListDropdown
                          name="countryCode"
                          isCountry
                          onChange={onCountryChange}
                          setFieldValue={setFieldValue}
                          helperText="Field requires three letter country abbreviations"
                          label="Country"
                          placeholder="ex USA"
                          onKeyPress={handleCountryFieldKeyPress}
                          disabled={isFreeUser}
                          required
                          borderLess
                        />
                      </AccountFormField>
                    </Box>
                    <Button
                      submit
                      loading={isSubmitting}
                      type={!dirty ? DISABLED_BUTTON : PRIMARY_BUTTON}
                      sx={{
                        maxWidth: '180px',
                        // float: 'right',
                        margin: '12px',
                      }}
                      fullWidth
                    >
                      Update
                    </Button>
                    {errors?.serverError && (
                      <Typography style={{ color: 'red', fontSize: 12, marginTop: 10 }} variant="body1">
                        {errors.serverError}
                      </Typography>
                    )}
                    <AlertSuccess submitted={submitted} style={{ right: '20%' }} />
                    {errors.emailOrPhoneRequired && (
                      <Typography style={{ color: 'red', fontSize: 12, marginTop: 10 }} variant="body1">
                        {errors.emailOrPhoneRequired}
                      </Typography>
                    )}
                  </Fragment>
                }
                center={
                  <Box
                    sx={{
                      height: '50%',
                      width: '1px',
                      marginLeft: '49%',
                      background: 'lightgrey',
                    }}
                  ></Box>
                }
                rightSide={
                  <Fragment>
                    <AvatarContainer>
                      {businessPhotoId && (
                        <Box width="150px" height="150px" display="flex" alignItems="center" justifyContent="center">
                          <img
                            src={
                              process.env['REACT_APP_BACKEND_BASE_AUTH_URL'] +
                              '/static/images/business/' +
                              businessPhotoId
                            }
                            style={{ maxWidth: '150px', maxHeight: '150px' }}
                            alt="business logo"
                          />
                        </Box>
                      )}
                      {!businessPhotoId && <BusinessAvatar model={business} width={130} height={130} />}
                      <input
                        accept="image/*"
                        name="avatarUrl"
                        id="upload-avatar"
                        type="file"
                        ref={inputFile}
                        style={{ display: 'none' }}
                        hidden
                        onChange={onFileFieldChange}
                        disabled={isFreeUser}
                      />

                      <Button fullWidth disabled={isFreeUser} onClick={onAvatarAddClick}>
                        Add Photo
                      </Button>
                      <Button
                        type={business?.details?.photoFileId ? SECONDARY_BUTTON : DISABLED_BUTTON}
                        fullWidth
                        negative
                        onClick={deleteBusinessAvatar}
                      >
                        Delete Photo
                      </Button>
                    </AvatarContainer>
                  </Fragment>
                }
              />
            </Form>
          )
        }}
      </Formik>
      <div>
        <ZendeskLink />
      </div>
    </>
  )
}
export default Profile
